export const PERMIT_STATUS_CODES = {
  DRAFT: "P0010",
  READY_TO_OPEN: "P0080",
  OPEN: "P0090",
  CLOSED: "P9009",
  SEND_BACK: "P0015",
  REQUESTER_INFORMATION: "P0251",
  CANCELLED: "P9000",
} as const

const STATUS_ALIASES: Record<string, string> = {
  APPROVED: PERMIT_STATUS_CODES.READY_TO_OPEN,
  READY_TO_OPEN: PERMIT_STATUS_CODES.READY_TO_OPEN,
  IN_PROGRESS: PERMIT_STATUS_CODES.OPEN,
  OPEN: PERMIT_STATUS_CODES.OPEN,
  COMPLETED: PERMIT_STATUS_CODES.CLOSED,
  CLOSED: PERMIT_STATUS_CODES.CLOSED,
  REJECTED: PERMIT_STATUS_CODES.SEND_BACK,
  REVISION: PERMIT_STATUS_CODES.SEND_BACK,
  RETURNED: PERMIT_STATUS_CODES.REQUESTER_INFORMATION,
  REQUESTER_INFORMATION: PERMIT_STATUS_CODES.REQUESTER_INFORMATION,
  CANCELLED: PERMIT_STATUS_CODES.CANCELLED,
  CANCELED: PERMIT_STATUS_CODES.CANCELLED,
  CANCEL: PERMIT_STATUS_CODES.CANCELLED,
  DRAFT: PERMIT_STATUS_CODES.DRAFT,
  P0001: PERMIT_STATUS_CODES.DRAFT,
  P0008: PERMIT_STATUS_CODES.READY_TO_OPEN,
  P0009: PERMIT_STATUS_CODES.OPEN,
  P9997: PERMIT_STATUS_CODES.SEND_BACK,
  P9998: PERMIT_STATUS_CODES.CANCELLED,
}

const STATUS_LABELS: Record<string, string> = {
  [PERMIT_STATUS_CODES.DRAFT]: "สร้างใหม่",
  P0001: "ร่าง",
  [PERMIT_STATUS_CODES.SEND_BACK]: "รอการแก้ไขจากผู้สร้าง",
  [PERMIT_STATUS_CODES.REQUESTER_INFORMATION]: "รอผู้ขอให้ข้อมูลเพิ่มเติม",
  P0020: "รอการตรวจสอบจาก ผู้ควบคุมงาน",
  P0021: "รอการตรวจสอบและอนุมัติจาก ผู้ควบคุมงาน / ENEL",
  P0022: "รอตรวจสอบและอนุมัติจากหน่วยงานที่เกี่ยวข้อง",
  P0023: "รอการตรวจสอบและอนุมัติจาก หน่วยงาน บำรุงรักษาประจำพื้นที่",
  P0024: "รอการตรวจสอบและอนุมัติจาก หน่วยงาน GA / ECC / รปภ.",
  P0025: "รอการตรวจสอบและอนุมัติจาก หน่วยงาน เจ้าของพื้นที่",
  P0026: "รอการตรวจสอบและอนุมัติจาก ผู้จัดการ หน่วยงานที่ต้องการบันทึกภาพ",
  P0027: "รอการตรวจสอบและอนุมัติจาก ผู้จัดการฝ่าย หน่วยงานที่ต้องการบันทึกภาพ",
  P0030: "รอการตรวจสอบจาก หัวหน้ากะ",
  P0040: "รอการอนุมัติจาก Shift Manager",
  P0050: "รอการอนุมัติจาก ผู้จัดการ เจ้าของพื้นที่",
  P0060: "รอการอนุมัติจาก ผู้จัดการฝ่าย เจ้าของพื้นที่",
  P0070: "รอการอนุมัติจาก ผู้ช่วยกรรมการผู้จัดการใหญ่",
  [PERMIT_STATUS_CODES.READY_TO_OPEN]: "รอการเปิด Permit",
  P0008: "รอเปิดงาน (ปัจจุบัน)",
  [PERMIT_STATUS_CODES.OPEN]: "เปิด Permit",
  P0009: "เปิด Permit",
  [PERMIT_STATUS_CODES.CLOSED]: "ปิด Permit",
  [PERMIT_STATUS_CODES.CANCELLED]: "ยกเลิก",
  P9998: "ยกเลิก",
  P9999: "ไม่ใช้งาน",
}

const WORKFLOW_STAGE_CODES = new Set<string>([
  "P0020",
  "P0021",
  "P0022",
  "P0023",
  "P0024",
  "P0025",
  "P0026",
  "P0027",
  "P0030",
  "P0040",
  "P0050",
  "P0060",
  "P0070",
  "P0080",
])

const READY_STATUSES = new Set<string>([PERMIT_STATUS_CODES.READY_TO_OPEN, "P0008"])
const OPEN_STATUSES = new Set<string>([PERMIT_STATUS_CODES.OPEN, "P0009"])
const CLOSED_STATUSES = new Set<string>([PERMIT_STATUS_CODES.CLOSED, "P9009"])
const REVISION_STATUSES = new Set<string>([PERMIT_STATUS_CODES.SEND_BACK])
const REQUESTER_INFORMATION_STATUSES = new Set<string>([PERMIT_STATUS_CODES.REQUESTER_INFORMATION])
const CANCELLED_STATUSES = new Set<string>([PERMIT_STATUS_CODES.CANCELLED, "P9998"])
const DRAFT_STATUSES = new Set<string>([PERMIT_STATUS_CODES.DRAFT, "P0001"])
const PRINTABLE_STATUSES = new Set<string>([
  PERMIT_STATUS_CODES.READY_TO_OPEN,
  PERMIT_STATUS_CODES.OPEN,
  PERMIT_STATUS_CODES.CLOSED,
  PERMIT_STATUS_CODES.REQUESTER_INFORMATION,
  "P0252",
  "P0253",
])

export type PermitStatusCategory =
  | "draft"
  | "workflow"
  | "ready"
  | "open"
  | "closed"
  | "revision"
  | "request-info"
  | "cancelled"
  | "other"

const STATUS_CATEGORY_CLASSES: Record<PermitStatusCategory, string> = {
  draft: "bg-gray-50 text-gray-700 border-gray-200",
  workflow: "bg-amber-50 text-amber-700 border-amber-200",
  ready: "bg-teal-50 text-teal-700 border-teal-200",
  open: "bg-sky-50 text-sky-700 border-sky-200",
  closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  revision: "bg-rose-50 text-rose-700 border-rose-200",
  "request-info": "bg-orange-50 text-orange-700 border-orange-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  other: "bg-slate-50 text-slate-700 border-slate-200",
}

const STATUS_TEXT_CLASSES: Record<PermitStatusCategory, string> = {
  draft: "text-gray-700",
  workflow: "text-amber-700",
  ready: "text-teal-700",
  open: "text-sky-700",
  closed: "text-emerald-700",
  revision: "text-rose-700",
  "request-info": "text-orange-700",
  cancelled: "text-slate-600",
  other: "text-slate-700",
}

const coerceUpper = (value?: string | null) => {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed.toUpperCase() : null
}

export const normalizePermitStatus = (status?: string | null): string | null => {
  const upper = coerceUpper(status)
  if (!upper) {
    return null
  }
  return STATUS_ALIASES[upper] ?? upper
}

export const getPermitStatusLabel = (status?: string | null, statusName?: string | null): string => {
  if (typeof statusName === "string" && statusName.trim().length > 0) {
    return statusName.trim()
  }

  const normalized = normalizePermitStatus(status)
  if (normalized && STATUS_LABELS[normalized]) {
    return STATUS_LABELS[normalized]
  }

  const raw = coerceUpper(status)
  if (raw && STATUS_LABELS[raw]) {
    return STATUS_LABELS[raw]
  }

  const fallback = (status ?? normalized ?? "").trim()
  if (!fallback) {
    return "ไม่ระบุสถานะ"
  }
  return fallback.replace(/_/g, " ")
}

export const FILTER_STATUS_CODES = [
  PERMIT_STATUS_CODES.DRAFT,
  PERMIT_STATUS_CODES.SEND_BACK,
  PERMIT_STATUS_CODES.REQUESTER_INFORMATION,
  "P0020",
  "P0021",
  "P0022",
  "P0023",
  "P0024",
  "P0025",
  "P0026",
  "P0027",
  "P0030",
  "P0040",
  "P0050",
  "P0060",
  "P0070",
  PERMIT_STATUS_CODES.READY_TO_OPEN,
  PERMIT_STATUS_CODES.OPEN,
  PERMIT_STATUS_CODES.CLOSED,
  PERMIT_STATUS_CODES.CANCELLED,
] as const

export const PERMIT_STATUS_FILTER_OPTIONS = FILTER_STATUS_CODES.map((code) => ({
  value: code,
  label: getPermitStatusLabel(code),
}))

export const isDraftStatus = (status?: string | null): boolean => {
  const normalized = normalizePermitStatus(status)
  if (normalized && DRAFT_STATUSES.has(normalized)) {
    return true
  }
  const raw = coerceUpper(status)
  return !!raw && DRAFT_STATUSES.has(raw)
}

export const isRevisionStatus = (status?: string | null): boolean => {
  const normalized = normalizePermitStatus(status)
  if (normalized && REVISION_STATUSES.has(normalized)) {
    return true
  }
  const raw = coerceUpper(status)
  return raw === "REJECTED" || raw === "REVISION"
}

export const isRequesterInformationStatus = (status?: string | null): boolean => {
  const normalized = normalizePermitStatus(status)
  if (normalized && REQUESTER_INFORMATION_STATUSES.has(normalized)) {
    return true
  }
  const raw = coerceUpper(status)
  return !!raw && REQUESTER_INFORMATION_STATUSES.has(raw)
}

export const isReadyToOpenStatus = (status?: string | null): boolean => {
  const normalized = normalizePermitStatus(status)
  if (normalized && READY_STATUSES.has(normalized)) {
    return true
  }
  const raw = coerceUpper(status)
  return !!raw && READY_STATUSES.has(raw)
}

export const isPrintablePermitStatus = (status?: string | null): boolean => {
  const normalized = normalizePermitStatus(status)
  if (normalized && PRINTABLE_STATUSES.has(normalized)) {
    return true
  }

  const raw = coerceUpper(status)
  return !!raw && (PRINTABLE_STATUSES.has(raw) || raw === "P0909")
}

export const isOpenStatus = (status?: string | null): boolean => {
  const normalized = normalizePermitStatus(status)
  if (normalized && OPEN_STATUSES.has(normalized)) {
    return true
  }
  const raw = coerceUpper(status)
  return !!raw && OPEN_STATUSES.has(raw)
}

export const isClosedStatus = (status?: string | null): boolean => {
  const normalized = normalizePermitStatus(status)
  if (normalized && CLOSED_STATUSES.has(normalized)) {
    return true
  }
  const raw = coerceUpper(status)
  return !!raw && CLOSED_STATUSES.has(raw)
}

export const isCancelledStatus = (status?: string | null): boolean => {
  const normalized = normalizePermitStatus(status)
  if (normalized && CANCELLED_STATUSES.has(normalized)) {
    return true
  }
  const raw = coerceUpper(status)
  return !!raw && (raw === "CANCELLED" || raw === "CANCELED" || raw === "CANCEL")
}

export const isWorkflowPendingStatus = (status?: string | null): boolean => {
  const normalized = normalizePermitStatus(status)
  if (normalized && WORKFLOW_STAGE_CODES.has(normalized)) {
    return true
  }
  const raw = coerceUpper(status)
  if (!raw) {
    return false
  }
  return WORKFLOW_STAGE_CODES.has(raw) || raw === "IN_WORKFLOW" || raw === "PENDING_APPROVAL"
}

export const isEditablePermitStatus = (status?: string | null): boolean => {
  return isDraftStatus(status) || isRevisionStatus(status) || isRequesterInformationStatus(status)
}

export const getPermitStatusCategory = (status?: string | null): PermitStatusCategory => {
  if (isDraftStatus(status)) {
    return "draft"
  }
  if (isRevisionStatus(status)) {
    return "revision"
  }
  if (isRequesterInformationStatus(status)) {
    return "request-info"
  }
  if (isReadyToOpenStatus(status)) {
    return "ready"
  }
  if (isOpenStatus(status)) {
    return "open"
  }
  if (isClosedStatus(status)) {
    return "closed"
  }
  if (isCancelledStatus(status)) {
    return "cancelled"
  }
  if (isWorkflowPendingStatus(status)) {
    return "workflow"
  }
  return "other"
}

export const getPermitStatusBadgeClass = (status?: string | null): string => {
  const category = getPermitStatusCategory(status)
  return `${STATUS_CATEGORY_CLASSES[category]} border`
}

export const getPermitStatusTextClass = (status?: string | null): string => {
  const category = getPermitStatusCategory(status)
  return STATUS_TEXT_CLASSES[category]
}
